// Client-side mirror of functions/engine.js's movement/capacity logic,
// adapted to the "myColumn"/"opponentColumn" shape the per-player view uses
// (rather than the server's player1Column/player2Column). Lets the board
// show the *actual* resulting state of a play -- including attraction pulls
// -- instead of guessing.
import { OASIS_CATALOG, ANIMAL_STRENGTH } from './oasisCatalog.js';

function otherSide(side) {
  return side === 'my' ? 'opponent' : 'my';
}

function columnKey(side) {
  return side === 'my' ? 'myColumn' : 'opponentColumn';
}

// Only *face-up* jackals count -- a face-down jackal doesn't open the 4th slot.
function jackalCount(column) {
  return column.filter(c => c.animal === 'jackal' && c.face === 'up').length;
}

// `incomingFace` only matters when `incomingAnimal` is 'jackal' -- a Jackal
// played face-down never counts toward the 3-face-up threshold, even if it
// would be the 3rd.
export function columnCapacity(column, incomingAnimal, incomingFace) {
  const incomingIsFaceUpJackal = incomingAnimal === 'jackal' && incomingFace === 'up';
  const count = jackalCount(column) + (incomingIsFaceUpJackal ? 1 : 0);
  return count >= 3 ? 4 : 3;
}

export function columnHasSpace(column, incomingAnimal, incomingFace) {
  return column.length < columnCapacity(column, incomingAnimal, incomingFace);
}

function baseCardStrength(card, column, oppositeColumn) {
  if (card.animal === 'chameleon') return null;
  let strength = ANIMAL_STRENGTH[card.animal];
  const jerboaPresent = column.some(c => c.animal === 'jerboa') || oppositeColumn.some(c => c.animal === 'jerboa');
  if (card.animal === 'elephant' && jerboaPresent) strength = 0;
  return strength;
}

/**
 * Mirrors functions/engine.js's resolveChameleonStrength: a Chameleon mimics
 * whatever card sits at the same depth in the adjacent oasis's column (left
 * or right, per its orientation) on the same side. Returns null if there's
 * nothing to resolve yet (no orientation chosen).
 */
export function resolveChameleonStrength(card, cardIndex, oasisRow, oasisIndex, side, visited = new Set()) {
  if (card.animal !== 'chameleon' || !card.orientation) return null;
  // Two (or more) chameleons can point at each other in a cycle, which has
  // no well-defined resolution -- treat a repeated visit as 0 rather than
  // recursing forever.
  const visitKey = `${side}:${oasisIndex}:${cardIndex}`;
  if (visited.has(visitKey)) return 0;
  visited.add(visitKey);

  const targetOasisIndex = card.orientation === 'left' ? oasisIndex - 1 : oasisIndex + 1;
  if (targetOasisIndex < 0 || targetOasisIndex >= oasisRow.length) return 0;

  const targetColumn = oasisRow[targetOasisIndex][columnKey(side)];
  const targetCard = targetColumn[cardIndex];
  if (!targetCard) return 0;
  if (targetCard.face === 'down') return 0;
  if (targetCard.animal === 'chameleon') {
    return resolveChameleonStrength(targetCard, cardIndex, oasisRow, targetOasisIndex, side, visited) ?? 0;
  }
  const oppositeColumn = oasisRow[targetOasisIndex][columnKey(otherSide(side))];
  return baseCardStrength(targetCard, targetColumn, oppositeColumn);
}

/**
 * Mirrors functions/engine.js's columnStrength: sums each face-up card's
 * effective strength (Jerboa/Camel-adjusted, Chameleon resolved via
 * mimicry), plus the 3-Camel bonus. Face-down cards don't count -- they're
 * only resolved at round end.
 */
export function columnStrength(column, oppositeColumn, oasisRow, oasisIndex, side) {
  let total = 0;
  const camelCount = column.filter(c => c.animal === 'camel' && c.face === 'up').length;

  column.forEach((card, index) => {
    if (card.face === 'down') return;
    if (card.animal === 'chameleon') {
      total += resolveChameleonStrength(card, index, oasisRow, oasisIndex, side) ?? 0;
      return;
    }
    total += baseCardStrength(card, column, oppositeColumn);
  });

  if (camelCount >= 3) total += 1;
  return total;
}

export function formedTriplet(column) {
  if (column.length < 3) return false;
  const counts = {};
  for (const card of column) {
    counts[card.animal] = (counts[card.animal] ?? 0) + 1;
    if (counts[card.animal] >= 3) return true;
  }
  return false;
}

function cloneOasisRow(oasisRow) {
  return oasisRow.map(o => ({
    ...o,
    palmTreeOwnerIds: [...o.palmTreeOwnerIds],
    myColumn: [...o.myColumn],
    opponentColumn: [...o.opponentColumn],
  }));
}

function movementCandidates(row, side, oasisIndex, card, isPortal) {
  const eligible = [];
  const collectFrom = (sourceOasisIndex, sourceSide) => {
    const col = row[sourceOasisIndex][columnKey(sourceSide)];
    col.forEach((c, cardIndex) => {
      if (c.animal === card.animal && c.face === 'up') {
        eligible.push({ cardId: c.cardId, sourceOasisIndex, sourceSide, cardIndex });
      }
    });
  };
  if (oasisIndex > 0) collectFrom(oasisIndex - 1, side);
  if (oasisIndex < row.length - 1) collectFrom(oasisIndex + 1, side);
  if (isPortal) collectFrom(oasisIndex, otherSide(side));
  return eligible;
}

/**
 * Previews whether placing `card` here would leave an ambiguous pull --
 * more eligible same-animal neighbors than the column has room for -- so
 * the player must choose exactly which ones move (see pullChoice on
 * placeCardAndResolveMovement) before the play can be finalized.
 */
export function getMovementChoice(oasisRow, side, oasisIndex, card) {
  if (card.face !== 'up') return { eligible: [], fitCount: 0, needsChoice: false };

  const row = cloneOasisRow(oasisRow);
  const targetColumn = row[oasisIndex][columnKey(side)];
  targetColumn.push(card);

  const isPortal = OASIS_CATALOG[row[oasisIndex].oasisId].isPortal;
  const eligible = movementCandidates(row, side, oasisIndex, card, isPortal);

  // Every eligible candidate is necessarily the same animal as card, so if
  // card is a Jackal, THIS pull's own contribution has to count toward the
  // 3-Jackal capacity check *before* deciding whether it fits -- not just
  // the Jackals already present from earlier iterations. Mirrors
  // columnCapacity's incomingAnimal hint.
  let length = targetColumn.length;
  let jackals = jackalCount(targetColumn);
  let fitCount = 0;
  for (let i = 0; i < eligible.length; i++) {
    const hypotheticalJackals = jackals + (card.animal === 'jackal' ? 1 : 0);
    const capacity = hypotheticalJackals >= 3 ? 4 : 3;
    if (length >= capacity) break;
    length += 1;
    jackals = hypotheticalJackals;
    fitCount += 1;
  }

  const distinctSources = new Set(eligible.map(e => `${e.sourceOasisIndex}:${e.sourceSide}`));
  const needsChoice = eligible.length > fitCount && fitCount > 0 && distinctSources.size > 1;

  return { eligible, fitCount, needsChoice };
}

/**
 * Places a card and resolves movement/attraction exactly like the server:
 * pulls same-type cards from adjacent columns on the same side, and (for
 * portal oases) straight through to the opposite side in the same column.
 * Only triggered by the newly-placed card -- no chaining. A face-down card
 * doesn't trigger any of this -- it just sits there until it's flipped
 * face-up at round end.
 *
 * `pullChoice` (optional): an array of cardIds, in the order they should be
 * pulled. Needed only when getMovementChoice reports needsChoice; otherwise
 * the default (nearest index first, own side before opposite side) is used.
 */
export function placeCardAndResolveMovement(oasisRow, side, oasisIndex, card, pullChoice) {
  const row = cloneOasisRow(oasisRow);
  const targetColumn = row[oasisIndex][columnKey(side)];
  targetColumn.push(card);

  if (card.face !== 'up') return row;

  const isPortal = OASIS_CATALOG[row[oasisIndex].oasisId].isPortal;
  const eligible = movementCandidates(row, side, oasisIndex, card, isPortal);

  // undefined = no explicit choice given, auto-pull everyone (default);
  // an array (even empty) = pull exactly these, in this order -- used by
  // the client to preview a partial/in-progress choice, where "nothing
  // chosen yet" must mean zero pulls, not the default full auto-pull.
  const order =
    pullChoice !== undefined
      ? pullChoice.map(cardId => eligible.find(e => e.cardId === cardId)).filter(Boolean)
      : eligible;

  for (const source of order) {
    // Capacity is recomputed fresh each iteration (not decremented from a
    // static value computed once up front) -- pulling in a Jackal can push
    // the column's own Jackal count to 3, which unlocks a 4th slot that a
    // *subsequent* pull should be able to use.
    if (targetColumn.length >= columnCapacity(targetColumn, card.animal, 'up')) break;
    const sourceColumn = row[source.sourceOasisIndex][columnKey(source.sourceSide)];
    const matchIndex = sourceColumn.findIndex(c => c.cardId === source.cardId);
    if (matchIndex === -1) continue;

    const [pulled] = sourceColumn.splice(matchIndex, 1);
    targetColumn.push(pulled);
    row[source.sourceOasisIndex][columnKey(source.sourceSide)] = sourceColumn;
  }

  return row;
}

/**
 * Flips an already-on-the-board face-down card in place and resolves its
 * movement/attraction exactly like a fresh play would -- used to preview a
 * round-end flip-order choice before it's actually submitted (see
 * OasisBoard.js), so picking a card visibly pulls in its matching
 * neighbors right away instead of waiting for the server to confirm it.
 * Chain with applyScorpion (same as a live Scorpion play) if the flipped
 * card turns out to be one.
 */
export function flipCardAndResolveMovement(oasisRow, side, oasisIndex, cardId, pullChoice) {
  const row = cloneOasisRow(oasisRow);
  const targetColumn = row[oasisIndex][columnKey(side)];
  const pos = targetColumn.findIndex(c => c.cardId === cardId);
  if (pos === -1) return row;
  const card = { ...targetColumn[pos], face: 'up' };
  targetColumn[pos] = card;

  const isPortal = OASIS_CATALOG[row[oasisIndex].oasisId].isPortal;
  const eligible = movementCandidates(row, side, oasisIndex, card, isPortal);

  const order =
    pullChoice !== undefined
      ? pullChoice.map(id => eligible.find(e => e.cardId === id)).filter(Boolean)
      : eligible;

  for (const source of order) {
    if (targetColumn.length >= columnCapacity(targetColumn, card.animal, 'up')) break;
    const sourceColumn = row[source.sourceOasisIndex][columnKey(source.sourceSide)];
    const matchIndex = sourceColumn.findIndex(c => c.cardId === source.cardId);
    if (matchIndex === -1) continue;

    const [pulled] = sourceColumn.splice(matchIndex, 1);
    targetColumn.push(pulled);
    row[source.sourceOasisIndex][columnKey(source.sourceSide)] = sourceColumn;
  }

  return row;
}

/**
 * Mirrors functions/engine.js's scorpionWeakestCandidates: every face-up
 * card (by printed strength, not column-adjusted effective strength) tied
 * for weakest on one side of a Scorpion's oasis, excluding the Scorpion
 * itself. More than one entry means this side is a genuine tie -- the
 * player has to choose which one dies (see OasisBoard.js's
 * pendingScorpionTieChoice), the same way an ambiguous attraction pull
 * already requires a pullChoice.
 */
export function scorpionWeakestCandidates(oasisRow, oasisIndex, scorpionCardId, side) {
  const column = oasisRow[oasisIndex][columnKey(side)];
  const eligible = column
    .map((card, index) => ({ card, index }))
    .filter(({ card }) => card.face === 'up' && card.cardId !== scorpionCardId);

  if (eligible.length === 0) return [];

  const strengths = eligible.map(({ card, index }) => ({
    index,
    cardId: card.cardId,
    animal: card.animal,
    strength:
      card.animal === 'chameleon'
        ? resolveChameleonStrength(card, index, oasisRow, oasisIndex, side) ?? 0
        : ANIMAL_STRENGTH[card.animal],
  }));

  const minStrength = Math.min(...strengths.map(s => s.strength));
  return strengths.filter(s => s.strength === minStrength);
}

/**
 * Mirrors functions/engine.js's scorpionTieIsTrivial: a tie only actually
 * needs a player's input when it could matter which card dies -- if every
 * tied candidate is the same animal AND they're all touching (contiguous
 * column positions), destroying any one of them is interchangeable, so no
 * choice is required.
 */
export function scorpionTieIsTrivial(weakest) {
  if (weakest.length <= 1) return true;
  if (!weakest.every(w => w.animal === weakest[0].animal)) return false;
  const indices = weakest.map(w => w.index).sort((a, b) => a - b);
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] - indices[i - 1] !== 1) return false;
  }
  return true;
}

/**
 * Mirrors functions/engine.js's applyScorpion: destroys the weakest face-up
 * card on each side of the oasis, excluding the scorpion itself.
 * `scorpionTieBreak` (optional) is `{ my?, opponent? }`, each a cardId --
 * when a side ties (2+ different candidates at the lowest strength) and
 * hasn't been chosen yet, that side is left untouched for now rather than
 * guessing, so the preview shows the *other*, unambiguous side (if any)
 * resolving immediately while the tied candidates just sit there, still on
 * the board, for OasisBoard.js to highlight as choices (pendingScorpion
 * TieChoice) -- exactly like an unresolved pull choice doesn't guess which
 * cards join either.
 */
export function applyScorpion(oasisRow, oasisIndex, scorpionCardId, scorpionTieBreak) {
  const row = cloneOasisRow(oasisRow);
  const entry = row[oasisIndex];

  for (const side of ['my', 'opponent']) {
    const key = columnKey(side);
    const weakest = scorpionWeakestCandidates(row, oasisIndex, scorpionCardId, side);
    if (weakest.length === 0) continue;

    let chosen = weakest[0];
    if (weakest.length > 1 && !scorpionTieIsTrivial(weakest)) {
      const chosenCardId = scorpionTieBreak?.[side];
      const match = chosenCardId ? weakest.find(w => w.cardId === chosenCardId) : undefined;
      if (!match) continue; // still ambiguous and not yet chosen -- leave this side alone for now
      chosen = match;
    }

    entry[key] = entry[key].filter((_, index) => index !== chosen.index);
  }

  return row;
}

function cardLocations(oasisRow) {
  const map = new Map();
  oasisRow.forEach((oasis, oasisIndex) => {
    for (const side of ['my', 'opponent']) {
      oasis[columnKey(side)].forEach(c => {
        map.set(c.cardId, { oasisIndex, side, animal: c.animal, face: c.face });
      });
    }
  });
  return map;
}

/**
 * Mirrors functions/gameFlow.js's diffBoard: compares two oasisRow
 * snapshots (before/after a move) and reports which cards moved (pulled
 * by attraction) or vanished (Scorpion), excluding whatever was freshly
 * placed this move (already known separately, via lastMoveCardIds).
 * Every card that can appear here was already face-up before the diff (a
 * pull/Scorpion kill can only ever touch a face-up card), so `animal` is
 * always safe to read even from the opponent's side -- unlike a placed
 * card, which might still be a redacted face-down mystery.
 */
export function diffOasisRow(beforeRow, afterRow, placedCardIds) {
  const before = cardLocations(beforeRow);
  const after = cardLocations(afterRow);

  const movedCards = [];
  const removedCards = [];

  for (const [cardId, from] of before) {
    if (placedCardIds.includes(cardId)) continue;
    const to = after.get(cardId);
    if (!to) {
      removedCards.push({ cardId, animal: from.animal, fromOasisIndex: from.oasisIndex, fromSide: from.side });
    } else if (to.oasisIndex !== from.oasisIndex || to.side !== from.side) {
      movedCards.push({
        cardId,
        animal: from.animal,
        fromOasisIndex: from.oasisIndex,
        fromSide: from.side,
        toOasisIndex: to.oasisIndex,
        toSide: to.side,
      });
    }
  }

  return { movedCards, removedCards };
}

/**
 * The inverse of a move: given the *current* (after) oasisRow and the
 * server-persisted breakdown of the last move that produced it
 * (placedCards/movedCards/removedCards -- see functions/gameFlow.js's
 * lastMoveResult/lastRoundResult), reconstructs what the board looked like
 * *before* that move. Lets the client re-derive a "before" snapshot to
 * animate/replay from even after a page refresh, when there's no longer
 * any in-memory history to diff against -- undone in the reverse of the
 * order the real move applied them (kill, then move, then placement).
 */
export function reconstructBeforeRow(afterRow, { placedCards = [], movedCards = [], removedCards = [] }) {
  const row = cloneOasisRow(afterRow);

  for (const r of removedCards) {
    const key = columnKey(r.fromSide);
    row[r.fromOasisIndex][key] = [
      ...row[r.fromOasisIndex][key],
      { cardId: r.cardId, animal: r.animal, face: 'up', orientation: null },
    ];
  }
  for (const m of movedCards) {
    const toKey = columnKey(m.toSide);
    const fromKey = columnKey(m.fromSide);
    const idx = row[m.toOasisIndex][toKey].findIndex(x => x.cardId === m.cardId);
    if (idx !== -1) {
      const [card] = row[m.toOasisIndex][toKey].splice(idx, 1);
      row[m.fromOasisIndex][fromKey] = [...row[m.fromOasisIndex][fromKey], card];
    }
  }
  for (const c of placedCards) {
    const key = columnKey(c.side);
    row[c.oasisIndex][key] = row[c.oasisIndex][key].filter(x => x.cardId !== c.cardId);
  }

  return row;
}
